import { useCallback, useEffect, useMemo, useState } from 'react';

import { useAppDispatch, useAppSelector } from '@src/core/hooks';
import {
  areAllFormInputsFilled,
  buildFormInputPayload,
  groupTariffUnits,
  organizeFormInputsBySaleItem,
  formInputGroupsWithErrors,
  type FormInputValues,
} from '@src/domains/orders';
import {
  COMPLETED,
  SENDING,
  clearRequiredFormInputsErrors,
  currentOrderSelector,
  orderRequiredFormInputsSelector,
  sendOrderRequiredFormInput,
} from '@src/features/orders/store/order';

import {
  ADDITIONAL_INFO_MODAL_MODE,
  type AdditionalInfoModalMode,
} from '@src/features/orders/form-inputs/components/AdditionalInfoModal';
import { buildTariffFormInputQueue } from '@src/features/orders/form-inputs/core/buildTariffFormInputQueue';
import { resolveSaleItemSummary } from '@src/features/orders/form-inputs/core/resolveSaleItemSummary';
import { resolveSaleGroupTitle } from '@src/features/orders/form-inputs/core/resolveSaleGroupTitle';
import { useFormInputsDraft } from '@src/features/orders/form-inputs/hooks/useFormInputsDraft';

type UseAdditionalInfoCollectParams = {
  onCompleted: () => void;
};

export const useAdditionalInfoCollect = ({ onCompleted }: UseAdditionalInfoCollectParams) => {
  const dispatch = useAppDispatch();
  const order = useAppSelector(currentOrderSelector);
  const formInputs = useAppSelector(orderRequiredFormInputsSelector);

  const formInputGroups = useMemo(() => formInputs?.inputs ?? [], [formInputs]);
  const storeErrors = useMemo(() => formInputs?.errors ?? {}, [formInputs]);

  const sales = useMemo(() => order?.sales ?? [], [order]);

  const { values, saveValues, clear } = useFormInputsDraft({ orderId: order?.id, expiresAt: order?.expiresAt });

  const saleGroups = useMemo(() => organizeFormInputsBySaleItem(formInputGroups, sales), [formInputGroups, sales]);
  const groupsWithErrors = useMemo(
    () => formInputGroupsWithErrors(formInputGroups, storeErrors),
    [formInputGroups, storeErrors],
  );

  /**
   * `undefined` means the buyer has not touched any accordion yet, so the first group
   * answers for it — the groups only get real ids once the order arrives, and storing
   * the id on the first render would leave every accordion collapsed after a refresh.
   * `null` is an explicit collapse.
   */
  const [chosenSaleId, setChosenSaleId] = useState<string | null | undefined>(undefined);

  const firstSaleItemId = saleGroups[0]?.saleItemId ?? null;
  const expandedSaleId = chosenSaleId === undefined ? firstSaleItemId : chosenSaleId;
  const [wizardOpen, setWizardOpen] = useState(false);
  const [mode, setMode] = useState<AdditionalInfoModalMode>(ADDITIONAL_INFO_MODAL_MODE.wizard);
  const [startKey, setStartKey] = useState('');
  const [editItemId, setEditItemId] = useState('');
  const [editTariffId, setEditTariffId] = useState('');
  /**
   * The rejected tariffs the wizard walks, taken when it opens. Fixing one clears its
   * errors, and a queue that shrank under the wizard would shift the step it is on.
   */
  const [rejectedKeys, setRejectedKeys] = useState<Set<string> | undefined>(undefined);

  const toggleSale = useCallback(
    (saleItemId: string) =>
      setChosenSaleId((current) => {
        const effective = current === undefined ? firstSaleItemId : current;

        return effective === saleItemId ? null : saleItemId;
      }),
    [firstSaleItemId],
  );

  const openTariff = useCallback(
    (saleItemId: string, tariffKey: string) => {
      setChosenSaleId(saleItemId);
      setMode(ADDITIONAL_INFO_MODAL_MODE.wizard);
      setStartKey(tariffKey);
      // After a 422 the wizard walks only the rejected tariffs, unless the buyer picked one that was not.
      setRejectedKeys(groupsWithErrors.has(tariffKey) ? new Set(groupsWithErrors) : undefined);
      setWizardOpen(true);
    },
    [groupsWithErrors],
  );

  /** Editing brings the units of that row at once, with no stepping. */
  const openTariffForEdit = useCallback((saleItemId: string, tariffId: string) => {
    setChosenSaleId(saleItemId);
    setMode(ADDITIONAL_INFO_MODAL_MODE.edit);
    setEditItemId(saleItemId);
    setEditTariffId(tariffId);
    setWizardOpen(true);
  }, []);

  const closeModal = useCallback(() => setWizardOpen(false), []);

  const queue = useMemo(() => {
    if (!wizardOpen) {
      return [];
    }

    if (mode === ADDITIONAL_INFO_MODAL_MODE.edit) {
      // Only the row that was clicked: bringing the whole item would show blank forms of
      // tariffs the buyer never asked to edit.
      const item = saleGroups.find((group) => group.saleItemId === editItemId);
      const tariff = item && groupTariffUnits(item.formInputGroups).find((entry) => entry.tariffId === editTariffId);

      if (!item || !tariff) {
        return [];
      }

      return buildTariffFormInputQueue({
        saleGroups: [{ saleItemId: item.saleItemId, formInputGroups: tariff.units }],
        startKey: '',
      });
    }

    return buildTariffFormInputQueue({ saleGroups, startKey, onlyKeys: rejectedKeys });
  }, [wizardOpen, mode, editItemId, editTariffId, saleGroups, startKey, rejectedKeys]);

  const payload = useMemo(() => buildFormInputPayload(formInputGroups, values), [formInputGroups, values]);
  const submitting = formInputs?.state === SENDING;
  const requiredCount = formInputs?.qtd ?? 0;
  const canContinue = requiredCount > 0 && areAllFormInputsFilled(payload, requiredCount) && !submitting;

  const submit = useCallback(() => {
    if (!canContinue) {
      return;
    }

    if (order) {
      dispatch(sendOrderRequiredFormInput(payload, order));
    }
  }, [dispatch, order, payload, canContinue]);

  const saveFormInput = useCallback(
    (patch: FormInputValues) => {
      saveValues(patch);
      dispatch(clearRequiredFormInputsErrors(Object.keys(patch)));
    },
    [saveValues, dispatch],
  );

  const summaryFor = useCallback((saleItemId: string) => resolveSaleItemSummary(sales, saleItemId), [sales]);

  useEffect(() => {
    if (formInputs?.state === COMPLETED) {
      clear();
      onCompleted();
    }
    // `clear` and `onCompleted` are kept out of the deps on purpose: only the state
    // transition should move the checkout on, or a new function identity repeats it.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intencional
  }, [formInputs?.state]);

  return {
    saleGroups,
    summaryFor,
    values,
    groupsWithErrors,
    expandedSaleId,
    toggleSale,
    modal: {
      open: wizardOpen && queue.length > 0,
      mode,
      queue,
      /** The product changes along the queue, since it crosses cart items. */
      productNameFor: (saleItemId: string) => resolveSaleGroupTitle(summaryFor(saleItemId)),
      /** The expanded accordion follows the wizard step. */
      onStepChange: setChosenSaleId,
    },
    openTariff,
    openTariffForEdit,
    closeModal,
    saveFormInput,
    canContinue,
    submitting,
    storeErrors,
    hasErrors: groupsWithErrors.size > 0,
    submit,
  };
};

export default useAdditionalInfoCollect;
